import { useEffect, useState } from 'react'
import { api } from './api'
import { useAbruf, Abrufausfall } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import { kurswert, periodenname, satz, t } from './sprache'
import { useDarf } from './Rechte'
import type { Kursvorschau } from './typen'

/** Die drei Wege zum Eintrittskurs (2.6 Abschnitt 2.11, R1403). */
export type Eintrittskursweg = 'eingetragen' | 'nachtragen' | 'stichtag'

/** Was an der Mitgliedschaft steht. */
export type Eintrittskursangabe = { weg: Eintrittskursweg | null; kurs: number | null; quelle: number | null }

/**
 * Die Felder, die gespeichert werden — `null`, solange die Wahl nicht
 * vollständig ist. Beim dritten Weg setzt der Server Kurs und Monat selbst
 * aus der Kurstabelle (R1404); die Seite schickt sie leer.
 */
export function felderDerWahl(weg: Eintrittskursweg | null, eingabe: string): Record<string, string> | null {
  if (weg === null) return null
  if (weg === 'eingetragen') {
    const kurs = Number(eingabe.trim().replace(/\./g, '').replace(',', '.'))
    if (!Number.isFinite(kurs) || kurs <= 0) return null
    return { eintrittskurs_weg: weg, eintrittskurs: String(kurs), eintrittskurs_quelle: '' }
  }
  return { eintrittskurs_weg: weg, eintrittskurs: '', eintrittskurs_quelle: '' }
}

/** Die Angabe in der Liste der Kreismitglieder — in einer Zeile. */
export function eintrittskurstext(angabe: Eintrittskursangabe): string {
  if (angabe.weg === 'nachtragen') return t('kreis.eintrittskurs.nachtragen.kurz')
  if (angabe.weg === 'stichtag' && angabe.kurs !== null && angabe.quelle !== null) {
    return t('kreis.eintrittskurs.stichtag.kurz', { kurs: kurswert(angabe.kurs), periode: periodenname(angabe.quelle) })
  }
  if (angabe.weg === 'eingetragen' && angabe.kurs !== null) return kurswert(angabe.kurs)
  return t('kreis.eintrittskurs.fehlt')
}

/**
 * **Der Eintrittskurs, ausdrücklich gewählt** (2.6 Abschnitt 2.11, R1402–R1405,
 * R1443) — einer von drei Wegen, nichts vorbelegt.
 *
 * Der Auftraggeber am 2026-10-07: *„Streng genommen wäre es richtig, wenn wir
 * bei einer Erstkonsolidierung die anzuwendenden historischen Kurse
 * ausdrücklich abfragen und nichts vom System aus still hinterlegen lassen."*
 *
 * Der dritte Weg nennt Monat und Wert, bevor er etwas tut, und warnt, wenn
 * der Kurs der Tabelle nicht echt ist (R1404). Fehlt er, holt ein eigener
 * Knopf ihn von der Zentralbank und zeigt das Ergebnis vor der Übernahme.
 */
export function Eintrittskurswahl({
  kreis,
  einheit,
  einheitsname,
  von,
  bisher,
  aufWahl,
}: {
  kreis: string
  einheit: string
  einheitsname: string
  von: number
  bisher: Eintrittskursangabe | null
  /** Meldet, ob die Gesellschaft einen Eintrittskurs braucht, und die Felder der Wahl. */
  aufWahl: (fremd: boolean, felder: Record<string, string> | null) => void
}) {
  const [weg, setWeg] = useState<Eintrittskursweg | null>(bisher?.weg ?? null)
  const [eingabe, setEingabe] = useState(bisher?.weg === 'eingetragen' && bisher.kurs !== null ? String(bisher.kurs).replace('.', ',') : '')
  const [versuch, setVersuch] = useState(0)
  const [zentralbank, setZentralbank] = useState<Kursvorschau | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const darf = useDarf()

  // Was der dritte Weg anbietet — der Inhalt des Abschnitts, ein Hauptabruf (R1211).
  const abruf = useAbruf(() => api.eintrittskursvorschlag(kreis, einheit, von), [kreis, einheit, von, versuch])
  const vorschlag = abruf.wert
  const stichtag = vorschlag?.stichtag ?? null

  useEffect(() => {
    if (!vorschlag) return
    const vollstaendig = weg === 'stichtag' && stichtag === null ? null : felderDerWahl(weg, eingabe)
    aufWahl(vorschlag.fremd, vorschlag.fremd ? vollstaendig : {})
  }, [vorschlag, weg, eingabe, stichtag, aufWahl])

  if (abruf.ausgefallen) return <Abrufausfall abruf={abruf} />
  if (!vorschlag || !vorschlag.fremd) return null

  const eingebracht = periodenname(vorschlag.eingebracht)

  async function holeVonDerZentralbank() {
    setMeldung(null)
    try {
      setZentralbank(await api.ezbkurse(vorschlag!.waehrung, vorschlag!.konzernwaehrung, vorschlag!.eingebracht, vorschlag!.eingebracht))
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    }
  }

  async function uebernimmVonDerZentralbank() {
    if (!zentralbank) return
    setMeldung(null)
    try {
      await api.kurseUebernehmen(zentralbank)
      setZentralbank(null)
      setVersuch((n) => n + 1)
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    }
  }

  const ezbStichtag = zentralbank?.zeilen.find((z) => z.art === 'Stichtag' && z.befund !== 'abgewiesen') ?? null

  return (
    <fieldset className="eintrittskurswahl">
      <legend>{t('kreis.mitglied.eintrittskurs')}</legend>
      <p className="erlaeuterung">
        {t('kreis.eintrittskurs.frage', {
          einheit: einheitsname,
          waehrung: vorschlag.waehrung,
          konzernwaehrung: vorschlag.konzernwaehrung,
          eingebracht,
        })}
      </p>

      <label className="wahl">
        <input type="radio" name={`eintritt-${einheit}`} checked={weg === 'eingetragen'} onChange={() => setWeg('eingetragen')} />
        <span>{t('kreis.eintrittskurs.weg.eingetragen')}</span>
      </label>
      {weg === 'eingetragen' && (
        <label className="feld">
          <span>{t('kreis.eintrittskurs.kurs', { waehrung: vorschlag.waehrung, konzernwaehrung: vorschlag.konzernwaehrung })}</span>
          <input type="text" inputMode="decimal" value={eingabe} onChange={(e) => setEingabe(e.target.value)} />
        </label>
      )}

      <label className="wahl">
        <input type="radio" name={`eintritt-${einheit}`} checked={weg === 'nachtragen'} onChange={() => setWeg('nachtragen')} />
        <span>{t('kreis.eintrittskurs.weg.nachtragen')}</span>
      </label>

      <label className="wahl">
        <input type="radio" name={`eintritt-${einheit}`} checked={weg === 'stichtag'} onChange={() => setWeg('stichtag')} />
        <span>{t('kreis.eintrittskurs.weg.stichtag')}</span>
      </label>
      {weg === 'stichtag' && stichtag !== null && (
        <div className="unterwahl">
          <p>{t('kreis.eintrittskurs.stichtag.wert', { periode: eingebracht, kurs: kurswert(stichtag) })}</p>
          {vorschlag.warnung && <p className="meldung">{satz(vorschlag.warnung)}</p>}
        </div>
      )}
      {weg === 'stichtag' && stichtag === null && (
        <div className="unterwahl">
          <p className="meldung">{t('kreis.eintrittskurs.stichtag.keiner', { periode: eingebracht, waehrung: vorschlag.waehrung })}</p>
          {/* Holen und Übernehmen nur mit dem Recht, Kurse zu übernehmen (R1180). */}
          {vorschlag.zentralbank && !zentralbank && darf('kurseUebernehmen') && (
            <button type="button" onClick={() => void holeVonDerZentralbank()}>
              {t('kreis.eintrittskurs.zentralbank', { periode: eingebracht })}
            </button>
          )}
          {zentralbank && (
            <>
              <p>
                {ezbStichtag
                  ? t('kreis.eintrittskurs.zentralbank.ergebnis', { periode: eingebracht, kurs: kurswert(ezbStichtag.wert) })
                  : t('kreis.eintrittskurs.zentralbank.nichts', { periode: eingebracht })}
              </p>
              {ezbStichtag && darf('kurseUebernehmen') && (
                <button type="button" onClick={() => void uebernimmVonDerZentralbank()}>
                  {t('kreis.eintrittskurs.zentralbank.uebernehmen')}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {meldung && <p className="meldung">{meldung}</p>}
    </fieldset>
  )
}
